import { useState, useEffect, useMemo } from 'react';
import { Search, X, Minus, Plus, LayoutGrid, Package } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { subscribePosProducts, subscribePosCategories, placePosOrder, posRemainingStock } from '../utils/posUtil';
import { POS_ALL_CATEGORIES, POS_SEARCH_MIN_CHARS } from '../constants/posConst';
import PosReceipt from './PosReceipt';
import Modal from './Modal';
import '../css/Pos.css';

/**
 * Product dashboard — the till.
 *
 * Laid out to the reference design: category cards with item counts across
 * the top, one search field under them, an image-led product grid, and the
 * order kept in a rail down the side with its own totals block.
 *
 * Tap a product to open the side panel, choose how many, add it. "Place
 * order" shows the whole thing as a receipt to confirm or cancel; confirming
 * is what decrements stock and writes the order (see placePosOrder).
 *
 * @returns {JSX.Element}
 */
function PosDashboard() {
  const { config } = useConfig();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(POS_ALL_CATEGORIES);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [panelQty, setPanelQty] = useState(1);
  // { [productId]: quantity } — the order being built.
  const [cart, setCart] = useState({});
  const [charging, setCharging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Cash tendered, as typed. Kept a string so the field can be empty — a
  // card or e-wallet sale leaves it blank and the receipt omits CASH/CHANGE.
  const [cashReceived, setCashReceived] = useState('');
  // The recorded order, shown as a receipt once the sale goes through. This
  // replaces the old "Order recorded" alert: a receipt is what staff and
  // customer actually need to see at that moment.
  const [placed, setPlaced] = useState(null);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const stopProducts = subscribePosProducts((list) => { setProducts(list); setLoading(false); });
    const stopCategories = subscribePosCategories(setCategories);
    return () => { stopProducts(); stopCategories(); };
  }, []);

  // Counts for the category cards. Taken from the WHOLE product list, not the
  // filtered one: a card saying "20 items" that changes as you type would be
  // reporting your search rather than the category.
  const countsByCategory = useMemo(() => {
    const counts = new Map();
    products.forEach((p) => {
      const key = p.category || '';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [products]);

  const visibleProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((product) => {
      if (activeCategory !== POS_ALL_CATEGORIES && product.category !== activeCategory) return false;
      if (term.length < POS_SEARCH_MIN_CHARS) return true;
      return `${product.name || ''} ${product.description || ''}`.toLowerCase().includes(term);
    });
  }, [products, activeCategory, search]);

  // Rebuilt from the live product list rather than stored: a price or name
  // edited in Inventory mid-order must not leave the rail quoting the old one.
  const cartLines = useMemo(() => Object.entries(cart)
    .map(([productId, quantity]) => {
      const product = products.find(p => p.id === productId);
      return product ? { product, quantity } : null;
    })
    .filter(Boolean), [cart, products]);

  const cartTotal = cartLines.reduce((sum, l) => sum + (Number(l.product.price) || 0) * l.quantity, 0);

  const draftOrder = {
    items: cartLines.map(({ product, quantity }) => ({
      productId: product.id,
      name: product.name,
      price: Number(product.price) || 0,
      quantity,
      lineTotal: (Number(product.price) || 0) * quantity,
    })),
    total: cartTotal,
  };

  // Floored at 0 so a short payment never shows negative change.
  const cashChange = cashReceived === '' ? null : Math.max(0, Number(cashReceived) - cartTotal);

  // What the panel may still add for the product it is showing. Recomputed
  // every render so it follows both the live stock figure and the order as it
  // is built up.
  const panelMax = selected ? posRemainingStock(selected, cart) : 0;
  const panelInCart = selected ? (cart[selected.id] || 0) : 0;

  /**
   * Holds a typed or stepped quantity inside 1..panelMax.
   *
   * Returns 0 when there is nothing left to add, which is the only case where
   * a quantity below 1 is meaningful — the Add button is disabled there, so
   * the field reads 0 rather than offering a 1 that cannot be honoured.
   *
   * @param {number|string} value - the requested quantity
   * @returns {number} a quantity the order can actually take
   */
  const clampPanelQty = (value) => {
    if (panelMax <= 0) return 0;
    const requested = Math.floor(Number(value)) || 0;
    return Math.min(panelMax, Math.max(1, requested));
  };

  const openProduct = (product) => {
    setSelected(product);
    // Opens at 1, or at 0 when the product is spoken for — clamped against
    // that product's own remaining stock, not the one previously open.
    setPanelQty(posRemainingStock(product, cart) > 0 ? 1 : 0);
  };

  const addToOrder = () => {
    if (!selected) return;
    // Re-clamped at the moment of adding rather than trusting panelQty: the
    // stock figure is live, so Inventory may have lowered it while the panel
    // sat open.
    const quantity = Math.min(panelQty, posRemainingStock(selected, cart));
    if (quantity <= 0) return;
    setCart(prev => ({ ...prev, [selected.id]: (prev[selected.id] || 0) + quantity }));
    setSelected(null);
  };

  const changeQty = (productId, delta) => setCart((prev) => {
    const next = { ...prev };
    const updated = (next[productId] || 0) + delta;
    if (updated <= 0) {
      delete next[productId];
      return next;
    }
    // The rail's + is a second way to raise a line, so it needs the same
    // ceiling the panel enforces — capped against the product's full stock
    // here, since this line's own quantity is what is being replaced.
    const stock = Math.max(0, Math.floor(Number(products.find(p => p.id === productId)?.supplies) || 0));
    next[productId] = Math.min(updated, stock);
    return next;
  });

  const handleConfirmCharge = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      const { id, order } = await placePosOrder(cartLines, { cashReceived: Number(cashReceived) });
      setCart({});
      setCharging(false);
      setCashReceived('');
      setPlaced({ id, order });
    } catch (error) {
      // Stays on the receipt: nothing was written and nothing was taken from
      // stock, so the staff can fix the quantity and confirm again.
      setModal({ title: 'Could not complete the sale', message: error.message, type: 'error' });
    } finally {
      setConfirming(false);
    }
  };

  if (loading) return <div className="pos-loading">{spinnerRing('Loading products…')}</div>;

  return (
    <div className="pos-dashboard">
      <div className="pos-dash-main">
        {/* ---------- Category cards ---------- */}
        <div className="pos-cat-rail" role="tablist">
          <button
            type="button" role="tab"
            aria-selected={activeCategory === POS_ALL_CATEGORIES}
            className={`pos-cat-card${activeCategory === POS_ALL_CATEGORIES ? ' active' : ''}`}
            onClick={() => setActiveCategory(POS_ALL_CATEGORIES)}
          >
            <span className="pos-cat-icon"><LayoutGrid size={18} /></span>
            <span className="pos-cat-name">All products</span>
            <span className="pos-cat-count">{products.length} items</span>
          </button>

          {categories.map((name) => (
            <button
              key={name} type="button" role="tab"
              aria-selected={activeCategory === name}
              className={`pos-cat-card${activeCategory === name ? ' active' : ''}`}
              onClick={() => setActiveCategory(name)}
            >
              <span className="pos-cat-icon"><Package size={18} /></span>
              <span className="pos-cat-name">{name}</span>
              <span className="pos-cat-count">{countsByCategory.get(name) || 0} items</span>
            </button>
          ))}
        </div>

        {/* ---------- Search ---------- */}
        <div className="pos-search-bar">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search for a product…"
            aria-label="Search products"
          />
          <Search size={17} aria-hidden="true" />
        </div>

        {/* ---------- Product grid ---------- */}
        {visibleProducts.length === 0 ? (
          <p className="pos-empty-line">No products to show.</p>
        ) : (
          <div className="pos-till-grid">
            {visibleProducts.map((product) => {
              const inCart = cart[product.id] || 0;
              const soldOut = (Number(product.supplies) || 0) <= 0;
              // Nothing left to add — either the shelf is empty or this order
              // already holds all of it. Both cases make the card a dead end,
              // so both are greyed out rather than opening a panel whose Add
              // button is disabled.
              const unavailable = posRemainingStock(product, cart) <= 0;
              const outLabel = soldOut ? 'Out of stock' : 'All in order';
              return (
                <button
                  key={product.id}
                  type="button"
                  className={`pos-till-card${unavailable ? ' out' : ''}`}
                  onClick={() => openProduct(product)}
                  disabled={unavailable}
                  title={unavailable ? `${product.name} — ${outLabel}` : product.name}
                >
                  <span className="pos-till-media">
                    {product.imageUrl
                      ? <img src={product.imageUrl} alt="" />
                      : <span className="pos-product-noimage">No image</span>}
                    {/* How many are already in the order, on the image where
                        it is visible without reading the rail. */}
                    {inCart > 0 && <span className="pos-till-badge">{inCart}</span>}
                    {unavailable && <span className="pos-till-out">{outLabel}</span>}
                  </span>
                  <span className="pos-till-name">{product.name}</span>
                  <span className="pos-till-meta">
                    {product.category
                      ? <span className="pos-till-chip">{product.category}</span>
                      : <span />}
                    <span className="pos-till-price">{formatMoney(Number(product.price || 0), config)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ---------- Order rail ---------- */}
      <aside className="pos-order-rail">
        <h2>Current order</h2>

        {cartLines.length === 0 ? (
          <p className="pos-empty-line">Nothing added yet.</p>
        ) : (
          <>
            <ul className="pos-order-lines">
              {cartLines.map(({ product, quantity }) => {
                const stock = Math.max(0, Math.floor(Number(product.supplies) || 0));
                return (
                <li key={product.id} className="pos-line">
                  <span className="pos-line-media">
                    {product.imageUrl
                      ? <img src={product.imageUrl} alt="" />
                      : <span className="pos-product-noimage">&nbsp;</span>}
                  </span>
                  <span className="pos-line-info">
                    <span className="pos-line-name">{product.name}</span>
                    <span className="pos-line-price">
                      {formatMoney(((Number(product.price) || 0) * quantity), config)}
                    </span>
                  </span>
                  <span className="pos-line-qty">
                    <button type="button" aria-label={`One less ${product.name}`} onClick={() => changeQty(product.id, -1)}>
                      <Minus size={12} />
                    </button>
                    <span>{quantity}</span>
                    {/* Stops at the shelf, same ceiling the panel applies. */}
                    <button
                      type="button"
                      aria-label={`One more ${product.name}`}
                      onClick={() => changeQty(product.id, 1)}
                      disabled={quantity >= stock}
                      title={quantity >= stock ? `Only ${stock} in stock` : undefined}
                    >
                      <Plus size={12} />
                    </button>
                  </span>
                </li>
                );
              })}
            </ul>

            <div className="pos-totals">
              <div className="pos-total-line">
                <span>Subtotal</span>
                <span>{formatMoney(cartTotal, config)}</span>
              </div>
              <div className="pos-total-line">
                <span>Items</span>
                <span>{cartLines.reduce((n, l) => n + l.quantity, 0)}</span>
              </div>
              <div className="pos-total-line pos-total-grand">
                <span>TOTAL</span>
                <span>{formatMoney(cartTotal, config)}</span>
              </div>
            </div>

            <button type="button" className="pos-btn pos-btn-primary pos-place-order" onClick={() => setCharging(true)}>
              Place order &middot; {formatMoney(cartTotal, config)}
            </button>
            <button type="button" className="pos-btn pos-btn-ghost" onClick={() => setCart({})}>Clear order</button>
          </>
        )}
      </aside>

      {/* ---------- Side panel ---------- */}
      {selected && (
        <div className="pos-panel" role="dialog" aria-label={selected.name}>
          <header className="pos-panel-head">
            <h3>{selected.name}</h3>
            <button type="button" onClick={() => setSelected(null)} aria-label="Close"><X size={18} /></button>
          </header>
          <div className="pos-panel-body">
            {selected.imageUrl && <img className="pos-panel-image" src={selected.imageUrl} alt="" />}
            {selected.description && <p className="pos-panel-desc">{selected.description}</p>}
            <p className="pos-panel-price">{formatMoney(Number(selected.price || 0), config)}</p>

            {/* Capped at what is actually on the shelf. The customer finding
                out at the counter that the till over-promised is worse than
                the stepper simply refusing to go higher. */}
            <label className="pos-field">
              <span>Quantity to add</span>
              <div className="pos-stepper">
                <button
                  type="button"
                  onClick={() => setPanelQty(q => clampPanelQty(q - 1))}
                  disabled={panelQty <= 1}
                  aria-label="Fewer"
                >
                  <Minus size={14} />
                </button>
                <input
                  type="number"
                  min="1"
                  max={panelMax}
                  value={panelQty}
                  onChange={(e) => setPanelQty(clampPanelQty(e.target.value))}
                  disabled={panelMax <= 0}
                />
                <button
                  type="button"
                  onClick={() => setPanelQty(q => clampPanelQty(q + 1))}
                  disabled={panelQty >= panelMax}
                  aria-label="More"
                >
                  <Plus size={14} />
                </button>
              </div>
            </label>

            {/* Says why the stepper stopped, so a capped field does not read
                as a broken one. */}
            {panelMax <= 0 ? (
              <p className="pos-panel-cap out">
                {(Number(selected.supplies) || 0) <= 0
                  ? 'Out of stock — nothing to add.'
                  : `All ${panelInCart} in stock are already in this order.`}
              </p>
            ) : panelInCart > 0 && (
              <p className="pos-panel-cap">
                {panelInCart} already in this order &middot; {panelMax} left to add.
              </p>
            )}

            <button
              type="button"
              className="pos-btn pos-btn-primary"
              onClick={addToOrder}
              disabled={panelMax <= 0 || panelQty <= 0}
            >
              Add to order
            </button>

            <hr className="pos-panel-rule" />

            {/* Read-only here on purpose. The till is for selling; stock is
                corrected in Inventory, where the change sits beside the rest
                of the product's details and goes through one save path. Two
                places to edit the same number is how the two end up
                disagreeing. */}
            <p className="pos-panel-stock">
              <span>Available supplies</span>
              <strong className={(Number(selected.supplies) || 0) <= 0 ? 'out' : undefined}>
                {Number(selected.supplies) || 0}
              </strong>
            </p>
            <p className="pos-panel-hint">Edit this in the Inventory tab.</p>
          </div>
        </div>
      )}

      {/* ---------- Confirmation ---------- */}
      {charging && (
        <div className="pos-modal-overlay">
          <div className="pos-modal pos-modal-receipt" role="dialog" aria-modal="true" aria-label="Confirm order">
            <header className="pos-modal-head">
              <h3>Confirm order</h3>
              <button type="button" className="pos-modal-close" onClick={() => setCharging(false)} aria-label="Close"><X size={18} /></button>
            </header>
            <div className="pos-modal-body">
              <PosReceipt
                order={{ ...draftOrder, cashReceived: Number(cashReceived) || null, change: cashChange }}
                courtName={config?.courtName}
                showPaymentNote
              />
              <label className="pos-field pos-cash-field">
                <span>Cash received (leave blank for card / e-wallet)</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  placeholder={String(cartTotal)}
                  inputMode="numeric"
                />
              </label>
              {/* Warned, not blocked: staff may be recording a part payment,
                  and the till should not argue with the person holding the
                  money. */}
              {cashReceived !== '' && Number(cashReceived) < cartTotal && (
                <p className="pos-cash-warn">
                  That is less than the total &mdash; short by {formatMoney((cartTotal - Number(cashReceived)), config)}.
                </p>
              )}
            </div>
            <footer className="pos-modal-foot">
              <span className="pos-foot-spacer" />
              <button type="button" className="pos-btn pos-btn-ghost" onClick={() => { setCharging(false); setCashReceived(''); }} disabled={confirming}>Cancel</button>
              <button type="button" className="pos-btn pos-btn-primary" onClick={handleConfirmCharge} disabled={confirming}>
                {confirming ? 'Recording…' : 'Confirm'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Recorded — the receipt itself, not a line of text about it. */}
      {placed && (
        <div className="pos-modal-overlay">
          <div className="pos-modal pos-modal-receipt" role="dialog" aria-modal="true" aria-label="Order receipt">
            <header className="pos-modal-head">
              <h3>Order recorded</h3>
              <button type="button" className="pos-modal-close" onClick={() => setPlaced(null)} aria-label="Close"><X size={18} /></button>
            </header>
            <div className="pos-modal-body">
              <PosReceipt order={placed.order} courtName={config?.courtName} reference={placed.id} />
            </div>
            <footer className="pos-modal-foot">
              <span className="pos-foot-spacer" />
              <button type="button" className="pos-btn pos-btn-primary" onClick={() => setPlaced(null)}>Done</button>
            </footer>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!modal}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
        confirmText="OK"
        onClose={() => setModal(null)}
      />
    </div>
  );
}

export default PosDashboard;
